import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../FirebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  Home,
  Search,
  ShoppingCart,
  MessageSquareMore,
  User,
  LogOut,
  ChevronDown,
  Lock,
  UserCheck,
  Store,
  Users,
  Utensils,
  ConciergeBell
} from 'lucide-react';

const Navbar = ({ isSigned, adminEmails, CampusEmails, setisSigned }) => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin]           = useState(false);
  const [isCampusRep, setIsCampusRep]   = useState(false);
  const [navOpen, setNavOpen]           = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [referralAvailable, setReferralAvailable] = useState(false);
  const [initial, setInitial]           = useState('');

  // ── Role state ──────────────────────────────────────────────────────────────
  // null  = still loading (show nothing)
  // 'seller' | 'buyer' | 'none' = resolved
  const [userRole, setUserRole]         = useState(null);

  // ── Framer Motion variants ──────────────────────────────────────────────────
  const menuVariants = {
    open:   { x: 0,      transition: { type: 'spring', stiffness: 220, damping: 25 } },
    closed: { x: '-100%',transition: { type: 'spring', stiffness: 220, damping: 25 } },
  };
  const overlayVariants = {
    open:   { opacity: 1 },
    closed: { opacity: 0 },
  };
  const modalVariants = {
    open:   { scale: 1,   opacity: 1, transition: { type: 'spring', stiffness: 150 } },
    closed: { scale: 0.8, opacity: 0, transition: { type: 'spring', stiffness: 150 } },
  };

  // ── Admin / campus-rep + initial ────────────────────────────────────────────
  useEffect(() => {
    const user = auth.currentUser;
    if (user?.email) {
      const email = user.email.toLowerCase();
      setInitial(email.charAt(0).toUpperCase());
      setIsAdmin(adminEmails.map(e => e.toLowerCase()).includes(email));
      setIsCampusRep(CampusEmails.map(e => e.toLowerCase()).includes(email));
    }
  }, [isSigned, adminEmails, CampusEmails]);

  // ── Fetch role from Firestore ───────────────────────────────────────────────
  useEffect(() => {
    if (!isSigned) {
      setUserRole(null);
      return;
    }
    const user = auth.currentUser;
    if (!user) { setUserRole('none'); return; }

    const fetchRole = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'profiles', user.uid));
        if (docSnap.exists()) {
          const role = docSnap.data().role;
          setUserRole(role === 'seller' || role === 'buyer' ? role : 'none');
        } else {
          setUserRole('none');
        }
      } catch {
        setUserRole('none'); // fail gracefully — don't block the UI
      }
    };

    fetchRole();
  }, [isSigned]);

  // ── Referral window ─────────────────────────────────────────────────────────
  useEffect(() => {
    const today     = new Date();
    const unlockDate = new Date('2026-05-15');
    const endDate    = new Date('2026-07-01');
    setReferralAvailable(today >= unlockDate && today < endDate);
  }, []);

  // ── Logout ──────────────────────────────────────────────────────────────────
  const handleLogout = () => {
    localStorage.removeItem('isSigned');
    setisSigned(false);
    if (window.innerWidth < 768) setNavOpen(false);
    setShowConfirm(false);
    setUserRole(null);
    navigate('/Login');
  };

  const confirmLogout = () => {
    setShowDropdown(false);
    setShowConfirm(true);
  };

  const toggleMenu = () => setNavOpen(v => !v);

  // ── Role-aware account links ─────────────────────────────────────────────────
  // null  → still fetching → render nothing (no flash)
  // buyer → Profile only
  // seller → Seller Dashboard only
  // none  → both Profile + Seller Dashboard
  const accountLinks = (LinkComp) => {
    if (userRole === null) return null; // loading — show nothing

    const items = [];

    if (userRole === 'buyer') {
      items.push(<LinkComp key="profile" to="/Profile" label="Profile" icon={User} />);
    } else if (userRole === 'seller') {
      items.push(<LinkComp key="seller" to="/seller" label="Seller Dashboard" icon={Store} />);
    } else {
      // 'none' — show both
      items.push(<LinkComp key="profile" to="/Profile" label="Profile" icon={User} />);
      items.push(<LinkComp key="seller" to="/seller" label="Seller Dashboard" icon={Store} />);
    }

    return items;
  };

  // ── Shared link components ───────────────────────────────────────────────────
  const NavLink = ({ to, icon, label }) => (
    <Link
      to={to}
      onClick={() => setNavOpen(false)}
      className="flex items-center gap-3 py-2 px-3 rounded-lg font-medium text-base text-slate-200 hover:bg-zinc-800/50 hover:text-green-400 transition-all duration-200"
    >
      {React.createElement(icon, { size: 20 })}
      <span>{label}</span>
    </Link>
  );

  const DropdownLink = ({ to, label, icon }) => (
    <Link
      to={to}
      onClick={() => setShowDropdown(false)}
      className="flex items-center gap-3 px-4 py-2 rounded-lg text-left hover:bg-gray-100 text-sm transition-colors duration-200"
    >
      {icon && React.createElement(icon, { size: 18, className: 'text-gray-500' })}
      <span className="text-gray-800">{label}</span>
    </Link>
  );

  // Tiny shimmer pill shown while role is loading (so layout doesn't jump)
  const RoleLoadingShimmer = () => (
    <div className="px-4 py-2 flex gap-2 items-center">
      <div className="h-3 w-20 rounded-full bg-gray-200 animate-pulse" />
    </div>
  );

  return (
    <div className="w-full bg-gradient-to-r from-zinc-900 to-zinc-800 shadow-2xl text-white px-4 md:px-8 py-4 flex justify-between items-center relative z-50">

      {/* Logo */}
      <Link to="/" className="flex items-center gap-3 group">
        <img
          src="https://i.ibb.co/847rXTfV/slazzer-preview-euzu3.png"
          alt="Logo"
          className="h-10 w-10 object-cover shadow-md transition-transform duration-300 group-hover:scale-105"
        />
        <span className="text-2xl font-bold text-white tracking-tight transition-colors duration-300 group-hover:text-green-400">
          u<span className="text-green-500">C</span>art
        </span>
      </Link>

      {/* Desktop Nav */}
      <nav className="hidden md:flex items-center gap-8 font-semibold text-base">
        <Link to="/"           className="nav-link-underline">Home</Link>
        <Link to="/product"    className="nav-link-underline">Products</Link>
        <Link to="/restuarant" className="nav-link-underline">Food</Link>
        <Link to="/service"    className="nav-link-underline">Services</Link>
        <Link to="/cart"       className="nav-link-underline">Cart</Link>
        {referralAvailable ? (
          <Link to="/JoinReferral" className="nav-link-underline">Referral</Link>
        ) : (
          <div className="relative group cursor-not-allowed">
            <span className="text-gray-500 py-2">Referral</span>
            <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-3 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow-md">
              Available soon
            </div>
          </div>
        )}
      </nav>

      {/* Desktop Account Button */}
      <div className="hidden md:block">
        {!isSigned ? (
          <Link
            to="/Login"
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-2 rounded-full font-semibold shadow-md transition-all duration-300"
          >
            Account
          </Link>
        ) : (
          <div className="relative">
            <button
              onClick={() => setShowDropdown(v => !v)}
              className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full px-4 py-2 font-semibold hover:from-green-600 hover:to-green-700 shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:ring-offset-zinc-900"
            >
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
                {initial}
              </span>
              <motion.span animate={{ rotate: showDropdown ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown size={16} />
              </motion.span>
            </button>

            <AnimatePresence>
              {showDropdown && (
                <motion.div
                  key="dropdown"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ type: 'spring', stiffness: 180, damping: 18 }}
                  className="absolute right-0 mt-3 bg-white rounded-xl shadow-2xl py-2 w-56 z-50 overflow-hidden border border-gray-200"
                >
                  {/* Role-based links — shimmer while loading */}
                  {userRole === null
                    ? <RoleLoadingShimmer />
                    : accountLinks(DropdownLink)
                  }

                  {isAdmin    && <DropdownLink to="/Admin"       label="Admin"        icon={Lock}  />}
                  {isCampusRep && <DropdownLink to="/campusAdmin" label="Campus Admin" icon={Users} />}

                  <button
                    onClick={confirmLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all text-sm mt-1"
                  >
                    <LogOut size={18} />
                    <span>Log out</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Mobile Hamburger */}
      <div className="md:hidden">
        <button onClick={toggleMenu} className="focus:outline-none p-2 rounded-full hover:bg-zinc-800 transition-colors duration-200">
          {navOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {navOpen && (
          <>
            <motion.div
              key="overlay"
              initial="closed" animate="open" exit="closed"
              variants={overlayVariants}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={toggleMenu}
            />

            <motion.div
              key="mobile-nav"
              initial="closed" animate="open" exit="closed"
              variants={menuVariants}
              className="fixed top-0 left-0 h-full w-4/5 max-w-sm bg-gradient-to-b from-zinc-900 to-zinc-800 text-white p-6 flex flex-col gap-8 z-50 shadow-2xl overflow-y-auto"
            >
              {/* Sidebar header */}
              <div className="flex justify-between items-center">
                <Link to="/" onClick={toggleMenu} className="flex items-center gap-3 group">
                  <img src="https://i.ibb.co/847rXTfV/slazzer-preview-euzu3.png" alt="uCart logo" className="h-10 w-10 shadow-md" />
                  <span className="text-2xl font-bold">u<span className="text-green-500">C</span>art</span>
                </Link>
                <button onClick={toggleMenu} className="p-2 rounded-full hover:bg-zinc-700 transition-colors duration-200">
                  <X size={24} />
                </button>
              </div>

              {/* Nav links */}
              <nav className="flex flex-col gap-2">
                <NavLink to="/"           icon={Home}         label="Home" />
                <NavLink to="/product"    icon={Search}       label="Products" />
                <NavLink to="/restuarant" icon={Utensils}     label="Food" />
                <NavLink to="/service"    icon={ConciergeBell}label="Services" />
                <NavLink to="/cart"       icon={ShoppingCart} label="Cart" />
                {referralAvailable ? (
                  <NavLink to="/JoinReferral" icon={UserCheck} label="Referral" />
                ) : (
                  <div className="relative group px-4 py-3">
                    <span className="flex items-center gap-4 text-gray-500 text-lg font-medium cursor-not-allowed">
                      <UserCheck size={24} className="text-gray-500" />
                      <span>Referral</span>
                    </span>
                    <div className="absolute left-1/2 -translate-x-1/2 bg-gray-700 text-white text-xs px-3 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow-md mt-1">
                      Available soon
                    </div>
                  </div>
                )}
              </nav>

              {/* Mobile account section */}
              <div className="mt-auto border-t border-zinc-700 pt-6">
                {!isSigned ? (
                  <NavLink to="/Login" icon={User} label="Account" />
                ) : (
                  <div className="flex flex-col gap-2 text-base">
                    {/* Role-based links — shimmer while loading */}
                    {userRole === null ? (
                      <div className="px-3 py-2 flex gap-2 items-center">
                        <div className="h-3 w-24 rounded-full bg-zinc-700 animate-pulse" />
                      </div>
                    ) : (
                      accountLinks(NavLink)
                    )}

                    {isAdmin     && <NavLink to="/Admin"       icon={Lock}  label="Admin" />}
                    {isCampusRep && <NavLink to="/campusAdmin" icon={Users} label="Campus Admin" />}

                    <button
                      onClick={confirmLogout}
                      className="flex items-center gap-4 py-3 px-4 rounded-lg text-red-400 hover:bg-red-900/20 transition-colors duration-200 text-base"
                    >
                      <LogOut size={20} />
                      <span>Log out</span>
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Logout confirmation modal */}
      <AnimatePresence>
        {showConfirm && (
          <motion.div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] backdrop-blur-sm"
            initial="closed" animate="open" exit="closed"
            variants={overlayVariants}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl p-6 w-[90%] max-w-sm text-center"
              initial="closed" animate="open" exit="closed"
              variants={modalVariants}
            >
              <h2 className="text-lg text-zinc-900 font-semibold mb-4">Confirm Logout?</h2>
              <div className="flex justify-between gap-4">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="bg-zinc-200 text-zinc-800 font-medium px-4 py-2 rounded-lg w-full hover:bg-zinc-300 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  className="bg-red-500 text-white font-medium px-4 py-2 rounded-lg w-full hover:bg-red-600 transition-colors duration-200"
                >
                  Log Out
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inline style for nav underline hover effect */}
      <style>{`
        .nav-link-underline {
          position: relative;
          padding-top: 0.5rem;
          padding-bottom: 0.5rem;
          transition: color 0.2s;
        }
        .nav-link-underline:hover { color: #4ade80; }
        .nav-link-underline::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 0; height: 2px;
          background: #4ade80;
          transition: width 0.3s;
        }
        .nav-link-underline:hover::after { width: 100%; }
      `}</style>
    </div>
  );
};

export default Navbar;